import {Button, Input, Select} from "@headlessui/react";
import React, {useEffect, useState} from "react";
import {ChevronDownIcon, XMarkIcon} from "@heroicons/react/16/solid";
import fipsCode from "../../JSONS/fipCodes.json";
import {
    ArrowDownCircleIcon, ArrowsPointingInIcon,
    ArrowsPointingOutIcon,
    ArrowUpCircleIcon, ClipboardDocumentCheckIcon,
    PlusCircleIcon
} from "@heroicons/react/24/outline";
import {useDarkMode} from "../../contexts/darkModeContext";
import {countryMapping, stateMapping} from "../GLOBAL/GlobalFunctions";
import FloatingInput from "../InputComponents/FloatingInput";
import FormDivider from "../Widgets/FormDivider";
import FloatingSelect from "../InputComponents/FloatingSelect";
import CustomButton from "../Widgets/CustomButton";
import FloatingTextArea from "../InputComponents/FloatingTextArea";

const AddressInput = ({ id, index, item, onRemove, onChange, Eaddress1, Eaddress2, Ecity, Ezip, Estate, Ecounty, Efips, darkMode = false }) => {

    const [address1, setAddress1] = useState(item?.address1 || "");
    const [address2, setAddress2] = useState(item?.address2 || "");
    const [city, setCity] = useState(item?.city || "");
    const [selectedState, setSelectedState] = useState(item?.selectedState || 'NJ'); // Preselect NJ
    const [zip, setZip] = useState(item?.zip || "");
    const [selectedCounty, setSelectedCounty] = useState(item?.selectedCounty || '');
    const [fips, setFips] = useState(item?.fips || "");
    const [country, setCountry] = useState(item?.country || "United States");
    const [contactName, setContactName] = useState(item?.contactName || "");
    const [contactPhone, setContactPhone] = useState(item?.contactPhone || "");
    const [shipEmail, setShippingEmail] = useState(item?.shipEmail || "");
    const [shipNotes, setShippingNotes] = useState(item?.shipNotes || "");
    const [shipPhone, setShipPhone] = useState(item?.shipPhone || "");
    

    const [isVisible, setIsVisible] = useState(true); // State to track visibility

    const [optionalVisible, setOptionalVisible] = useState(false);
    
    // Function to toggle visibility
    const toggleVisibility = () => {
        setIsVisible(!isVisible);
    };

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    useEffect(() => {
        if (contactName.trim() !== '' && contactPhone.toString().trim() !== '') {
            setIsVisible(true); // Set visibility to true if both inputs are filled
        } else {
            setIsVisible(true); // Set visibility to false otherwise
        }
    }, [contactName, contactPhone]); // Runs whenever fname or lname changes

    const [isLoaded, setIsLoaded] = useState(false); // State to track when the component is loaded
    const [isRemoving, setIsRemoving] = useState(false); // Track whether the item is being removed

    // Handle the remove action with animation
    const handleRemove = () => {
        setIsRemoving(true); // Trigger the animation
        setTimeout(() => onRemove(id), 500); // Wait for the animation to complete before removing (adjust timing to match animation)
    };
    
    const handleCountyChange = (event) => {
        const countyName = event.target.value;
        setSelectedCounty(countyName);

        // Find the selected county's data based on the selected county name
        const countyData = filteredCounties.find(county => county.county === countyName);

        // Set the fips code if county data exists, otherwise set it to an empty string
        if (countyData) {
            setFips(countyData.fips);
        } else {
            setFips("");  // Handle case where no county is selected or found
        }
    };

    // Filter counties based on the selected state
    const filteredCounties = fipsCode.county.filter(county => county.state === selectedState);

    const formatFipsCode = (fips) => {
        if (fips.length === 5) {
            return `${fips.slice(0, 2)}-${fips.slice(2)}`;
        }
        return fips;
    };
    
    
    
    const [isChecked, setIsChecked] = useState(false);

     const handleCheckboxChange = () => {
        setIsChecked(prev => {
            const newCheckedState = !prev;
            
            setAddress1(newCheckedState ? Eaddress1 : ""); // Set or reset nickName
            setAddress2(newCheckedState ? Eaddress2 : ""); // Set or reset nickName
            setCity(newCheckedState ? Ecity : ""); // Set or reset nickName
            setSelectedState(newCheckedState ? Estate : ""); // Set or reset nickName
            setZip(newCheckedState ? Ezip : ""); // Set or reset nickName
            setSelectedCounty(newCheckedState ? Ecounty : ""); // Set or reset nickName
            setFips(newCheckedState ? Efips : ""); // Set or reset nickName
            
            return newCheckedState;
        });
    };



    useEffect(() => {
        onChange(index, {
            address1,
            address2,
            city,
            selectedState,
            zip,
            selectedCounty,
            fips,
            country,
            contactName,
            contactPhone,
            shipEmail,
            shipNotes,
        });
        // eslint-disable-next-line
    }, [address1, address2, city, selectedState, zip, selectedCounty, fips, country, contactName, contactPhone, shipEmail, shipNotes]); // Update on change



    return (
        <div className={`w-full flex flex-col border ${darkMode ? 'border-neutral-700 text-white' : 'text-black'} rounded-lg transition-transform duration-500 ease-in-out ${isRemoving ? 'opacity-50 transform scale-75 rotate-1 border-red-500' : ''} ${isLoaded ? '' : 'translate-x-full opacity-0'}`}>

            {/*HEADER*/}
            <div className={`w-full flex flex-row gap-x-2 items-center p-2 border-b ${darkMode ? 'border-b-neutral-700' : ''}`}>

                {/*Field Grid*/}
                <div className="w-full grid grid-cols-6 gap-x-4">
                    
                    {/*Contact Name*/}
                    <div className={`relative col-span-3`}>
                        
                        <FloatingInput
                            label="Contact Name"
                            required={true}
                            placeholder="Enter Contact Name..."
                            value={contactName}
                            darkMode={darkMode}
                            onChange={(e) => setContactName(e.target.value)}
                        />
                        
                    </div>
                    
                    {/*ContactPhone*/}
                    <div className={`relative col-span-3`}>
                        
                        <FloatingInput
                            label="Contact Phone"
                            required={true}
                            placeholder="Enter Phone Number..."
                            value={contactPhone}
                            darkMode={darkMode}
                            onChange={(e) => setContactPhone(e.target.value)}
                        />
                        
                    </div>
                    
                </div>
                
                {/*Buttons*/}
                <div className={`flex divide-y flex-col items-center rounded-lg w-fit ${darkMode ? 'bg-white/5 divide-neutral-700' : 'bg-black/5 divide-gray-300'}`}>
                    <Button
                        className="size-8 flex items-center justify-center"
                        onClick={handleRemove}
                    >
                        <XMarkIcon className="size-6 text-gray-500 hover:text-red-600 hover:scale-110"/>
                    </Button>

                    <Button
                        className="size-8 flex items-center justify-center"
                        onClick={toggleVisibility}
                    >
                        <ChevronDownIcon className={`text-gray-500 hover:text-green-600 hover:scale-110 size-6 ${isVisible ? '' : 'rotate-180'} duration-500`}/>
                    </Button>
                </div>

            </div>

            {/* Conditionally render content based on isVisible */}
            {isVisible && (
                <div className={`w-full px-4 py-4 grid grid-cols-6 gap-x-4 gap-y-4`}>
                    
                    
                    {/*Shipping Info Header*/}
                    <div className="py-4 sm:col-span-full relative justify-center items-center font-semibold uppercase flex">
                       
                        <div className="relative flex justify-start w-fit">
                            <span className={`${darkMode ? 'bg-bgDark' : 'bg-white'} pr-3 text-base font-semibold italic whitespace-nowrap`}>Shipping Info [Optional]</span>
                        </div>
                        
                        <div aria-hidden="true" className={`w-full border-t ${darkMode ? 'border-neutral-700' : 'border-gray-300'}`}/>
                        
                        <div className={`w-fit whitespace-nowrap pl-2`}>
                            <CustomButton
                                color={`green`}
                                onClick={() => setOptionalVisible(!optionalVisible)}
                                icon={optionalVisible ? ArrowsPointingInIcon : ArrowsPointingOutIcon}
                                text={optionalVisible ? 'Collapse' : 'Expand'}
                            />
                        </div>
                        
                    </div>
                    
                    
                    {optionalVisible && (
                        
                        <div className={`grid grid-cols-6 gap-x-4 gap-y-4 col-span-full`}>
                            
                            {/*Shipping Email*/}
                            <div className={`relative col-span-3`}>
                                
                                <FloatingInput
                                    label="Ship To Email"
                                    required={false}
                                    placeholder="Optional..."
                                    value={shipEmail}
                                    darkMode={darkMode}
                                    onChange={(e) => setShippingEmail(e.target.value)}
                                />
                                
                            </div>
                            
                            {/*Shipping Phone*/}
                            <div className={`relative col-span-3`}>
                                
                                <FloatingInput
                                    label="Ship To Phone Number"
                                    required={false}
                                    placeholder="Optional..."
                                    value={shipPhone}
                                    darkMode={darkMode}
                                    onChange={(e) => setShipPhone(e.target.value)}
                                />
                                
                            </div>
                            
                            {/*Shipping Notes*/}
                            <div className={`relative col-span-full`}>
                                
                                <FloatingTextArea
                                    label="Ship To Notes"
                                    required={false}
                                    placeholder="Optional..."
                                    value={shipNotes}
                                    darkMode={darkMode}
                                    onChange={(e) => setShippingNotes(e.target.value)}
                                />
                                
                            </div>
                            
                        </div>
                        
                    )}
                    
                    
                    {/*Address Header*/}
                    <div className="py-4 sm:col-span-full relative justify-center items-center font-semibold uppercase flex">
                       
                        <div className="relative flex justify-start w-fit">
                            <span className={`${darkMode ? 'bg-bgDark' : 'bg-white'} pr-3 text-base font-semibold italic whitespace-nowrap`}>Address</span>
                        </div>
                        
                        <div aria-hidden="true" className={`w-full border-t ${darkMode ? 'border-neutral-700' : 'border-gray-300'}`}/>
                        
                        <div className={`w-fit whitespace-nowrap pl-2`}>
                            <CustomButton
                                color={`green`}
                                onClick={handleCheckboxChange}
                                icon={ClipboardDocumentCheckIcon}
                                text={isChecked ? 'Back To Custom' : 'Same As Billing'}
                            />
                        </div>
                        
                    </div>
                    
                    
                    {/*Address 1*/}
                    <div className={`relative col-span-3`}>
                        
                        <FloatingInput
                            label="Address [1]"
                            required={true}
                            placeholder="Street Address 1..."
                            value={address1}
                            darkMode={darkMode}
                            onChange={(e) => setAddress1(e.target.value)}
                        />
                        
                    </div>
                    
                    {/*Address 2*/}
                    <div className={`relative col-span-3`}>
                        
                        <FloatingInput
                            label="Address [2]"
                            required={false}
                            placeholder="Optional..."
                            value={address2}
                            darkMode={darkMode}
                            onChange={(e) => setAddress2(e.target.value)}
                        />
                        
                    </div>
                    
                    {/*City*/}
                    <div className={`relative col-span-2`}>
                        
                        <FloatingInput
                            label="City"
                            required={true}
                            placeholder="Enter City..."
                            value={city}
                            darkMode={darkMode}
                            onChange={(e) => setCity(e.target.value)}
                        />
                        
                    </div>
                    
                    {/*State*/}
                    <div className={`relative col-span-2`}>
                        
                        <FloatingSelect
                            label="State"
                            value={selectedState}
                            darkMode={darkMode}
                            onChange={(e) => setSelectedState(e.target.value)}
                            required={country === "United States"}
                        >
                            {Object.entries(stateMapping).map(([abbr, name]) => (
                                <option key={abbr} value={abbr}>
                                  {name}
                                </option>
                            ))}
                        </FloatingSelect>
                        
                    </div>
                    
                    {/*ZipCode*/}
                    <div className={`relative col-span-2`}>
                        
                        <FloatingInput
                            label="Zip Code"
                            required={country === "United States"}
                            placeholder="Enter Zip Code..."
                            value={zip}
                            darkMode={darkMode}
                            onChange={(e) => setZip(e.target.value)}
                        />
                        
                    </div>
                    
                    {/*Country*/}
                    <div className={`relative col-span-full`}>
                        
                        <FloatingSelect
                            label="Country"
                            value={country}
                            darkMode={darkMode}
                            onChange={(e) => setCountry(e.target.value)}
                            required={true}
                        >
                            {Object.entries(countryMapping).map(([abbr, name]) => (
                                <option key={abbr} value={abbr}>
                                  {name}
                                </option>
                            ))}
                        </FloatingSelect>
                        
                    </div>
                    
            
                    <FormDivider label={`Fips Code`} darkMode={darkMode}/>
            
                    
                    {/*County*/}
                    <div className={`relative col-span-4`}>
                        
                        <FloatingSelect
                            label="County"
                            value={selectedCounty}
                            onChange={handleCountyChange}
                            required={country === "United States"}
                            darkMode={darkMode}
                        >
                            <option value="">Select a County...</option>
                            {filteredCounties.map(county => (
                                <option key={county.fips} value={county.county}>{county.county}</option>
                            ))}
                        </FloatingSelect>
                        
                    </div>
                    
                    {/*Fips Code*/}
                    <div className={`relative col-span-2`}>
                        
                        <FloatingInput
                            label="Fips Code"
                            required={country === "United States"}
                            disabled={true}
                            placeholder="Fips Code..."
                            value={formatFipsCode(fips)}
                            darkMode={darkMode}
                        />
                        
                    </div>
                    
                    

                </div>
            )}


        </div>
    )
}

export default AddressInput;
